import React, { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { TextAlign } from "@tiptap/extension-text-align";
import Paragraph from "@tiptap/extension-paragraph";
import { Highlight } from "@tiptap/extension-highlight";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { MathExtension } from "../extensions/MathExtension";
import { CustomImageExtension } from "../extensions/CustomImageExtension";
import {
  Fraction,
  MixedFraction,
  Sqrt,
  NthRoot,
  Power,
  Subscript,
} from "../extensions/InlineFormulaExtension";
import {
  MathDirectionExtension,
  MathExpressionMark,
} from "../extensions/MathDirectionExtension";
import EditorToolbar from "./EditorToolbar";

// عرض کاغذ A4 برای ریسپانسیو (210mm ≈ 794px در 96dpi)
const SHEET_WIDTH_PX = 794;

/**
 * محیط نوشتن یک سوال روی قاب A4
 *
 *   [تولبار — بیرون از کاغذ]
 *   [قاب A4: محل تایپ سوال — هر برگه یک سوال]
 *
 * قاب A4 صرفاً به معلم «حس اندازه واقعی» می‌دهد؛ خروجی ذخیره‌شده
 * HTML خوداتکای سوال است (بدون سربرگ/شماره صفحه).
 */
const A4ExamEditor = ({ value, onChange }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        paragraph: false,
      }),
      Paragraph.extend({
        whitespace: "pre",
      }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Highlight.configure({ multicolor: true }),
      TextStyle,
      Color,
      CustomImageExtension.configure({ inline: true, allowBase64: true }),
      MathExtension,
      Fraction,
      MixedFraction,
      Sqrt,
      NthRoot,
      Power,
      Subscript,
      MathExpressionMark,
      MathDirectionExtension,
    ],
    content: value,
    parseOptions: {
      preserveWhitespace: "full",
    },
    onUpdate: ({ editor }) => {
      if (!editor.isDestroyed && onChange) onChange(editor.getHTML());
    },
    editorProps: {
      parseOptions: {
        preserveWhitespace: "full",
      },
      attributes: {
        class: "focus:outline-none exam-body-content dir-rtl text-right",

        dir: "rtl",
      },
    },
  });

  // همگام‌سازی مقدار خارجی (پاک شدن ادیتور پس از ثبت)
  useEffect(() => {
    if (editor && !editor.isDestroyed && value !== editor.getHTML()) {
      if (!editor.isFocused) {
        editor.commands.setContent(value, false, {
          preserveWhitespace: "full",
        });
      }
    }
  }, [value, editor]);

  /**
   * ریسپانسیو: قاب A4 عرض ثابت دارد؛ در صفحه باریک‌تر کوچک می‌شود.
   */
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [frameHeight, setFrameHeight] = useState(1123);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const compute = () => {
      setScale(Math.min(1, wrap.clientWidth / SHEET_WIDTH_PX));
    };
    compute();
    const obs = new ResizeObserver(compute);
    obs.observe(wrap);
    return () => obs.disconnect();
  }, []);

  // ارتفاع قاب بر اساس ارتفاع واقعی محتوا (حداقل یک برگه)
  useEffect(() => {
    const target = document.querySelector(".ProseMirror");
    if (!target) return;
    const update = () => {
      setFrameHeight(Math.max(1123, target.scrollHeight + 120));
    };
    update();
    const obs = new ResizeObserver(update);
    obs.observe(target);
    return () => obs.disconnect();
  }, []);

  return (
    <div className="a4-exam-editor">
      {/* تولبار */}
      <div className="mb-3">
        <EditorToolbar editor={editor} />
      </div>

      {/* قاب A4 — در موبایل کوچک‌نمایی می‌شود */}
      <div
        ref={wrapRef}
        className="a4-scale-wrap"
        style={{
          overflow: "hidden",
          direction: "ltr",
        }}
      >
        <div
          style={{
            transform: `scale(${scale})`,
            transformOrigin: "top left",
            width: SHEET_WIDTH_PX,
            margin: scale >= 1 ? "0 auto" : undefined,
          }}
        >
          <div
            className="relative a4-sheet-wrapper bg-white border border-slate-300 shadow-xl mx-auto"
            style={{
              width: "210mm",
              padding: "10mm 10mm",
              boxSizing: "border-box",
            }}
          >
            <EditorContent editor={editor} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default A4ExamEditor;
